/**
 * CallHistoryList — filterbare tabel met belhistorie op /telefoon.
 *
 * Consumeert /api/telephony/history met filters + paginering. Klik op rij
 * selecteert een call en triggert <CallDetailPanel> via onSelect.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { TakenKlantDrawer } from './TakenKlantDrawer';

/**
 * Wie het gesprek afhandelde. Server-side afgeleid uit answered_by +
 * routing_stage + status (api/_lib/call-afhandeling.ts) — bewust niet hier,
 * anders drift de lijst weg van het detailpaneel.
 */
interface Afhandeling {
  soort: 'agent' | 'medewerker' | 'voicemail' | 'doorgeschakeld' | 'gemist' | null;
  naam: string | null;
}

interface HistoryRow {
  id: string;
  conversationId: string | null;
  direction: 'inbound' | 'outbound';
  fromNumber: string | null;
  toNumber: string | null;
  brand: string;
  status: string;
  startedAt: string;
  answeredAt: string | null;
  durationSeconds: number | null;
  recordingUrl: string | null;
  packetLossPct: number | null;
  customerName: string | null;
  customerPhone: string | null;
  customerId: string | null;
  customerType: 'account' | 'lead' | null;
  afhandeling: Afhandeling | null;
}

interface HistoryResponse {
  calls: HistoryRow[];
  total: number;
  page: number;
  limit: number;
}

interface Filters {
  q: string;
  brand: string;
  status: string;
  direction: string;
  afhandeling: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = {
  q: '', brand: '', status: '', direction: '', afhandeling: '', from: '', to: '',
};
const LIMIT = 50;

const BRAND_OPTIONS = ['', 'CINCO', 'KLEDA', 'SOFASAVER', 'SCHOONHUISSHOP', 'VLOERKLEEDREINIGEN', 'BANKREINIGEN'];
const STATUS_OPTIONS = ['', 'completed', 'voicemail', 'missed', 'forwarded', 'rejected', 'failed', 'active', 'ringing'];
const AFHANDELING_OPTIONS: Array<[string, string]> = [
  ['', 'Alle afhandelaars'],
  ['agent', 'Voice-agent'],
  ['medewerker', 'Medewerker'],
  ['voicemail', 'Voicemail'],
  ['doorgeschakeld', 'Doorgeschakeld'],
  ['gemist', 'Niemand (gemist)'],
];

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function qualityLabel(pct: number | null): { text: string; color: string } {
  if (pct === null || pct === undefined) return { text: '-', color: 'text-slate-400' };
  if (pct < 2) return { text: `${pct.toFixed(1)}%`, color: 'text-emerald-600' };
  if (pct < 5) return { text: `${pct.toFixed(1)}%`, color: 'text-amber-600' };
  return { text: `${pct.toFixed(1)}%`, color: 'text-red-600' };
}

function statusBadge(
  status: string,
  row?: { direction: string; answeredAt: string | null },
): { label: string; cls: string } {
  // Uitgaand en nooit opgenomen wordt als 'completed' gefinaliseerd (missed is
  // een inbound-begrip) — zonder dit onderscheid stond een niet-opgenomen
  // uitgaand gesprek groen als "Beantwoord" in de historie.
  if (status === 'completed' && row && row.direction === 'outbound' && !row.answeredAt) {
    return { label: 'Geen gehoor', cls: 'bg-slate-100 text-slate-600' };
  }
  switch (status) {
    case 'completed': return { label: 'Beantwoord', cls: 'bg-emerald-50 text-emerald-700' };
    case 'voicemail': return { label: 'Voicemail', cls: 'bg-purple-50 text-purple-700' };
    case 'missed': return { label: 'Gemist', cls: 'bg-red-50 text-red-700' };
    case 'forwarded': return { label: 'Doorgeschakeld', cls: 'bg-sky-50 text-sky-700' };
    case 'rejected': return { label: 'Geweigerd', cls: 'bg-amber-50 text-amber-700' };
    case 'failed': return { label: 'Mislukt', cls: 'bg-red-50 text-red-700' };
    case 'active': return { label: 'Actief', cls: 'bg-indigo-50 text-indigo-700' };
    case 'ringing': return { label: 'Rinkelt', cls: 'bg-slate-50 text-slate-600' };
    default: return { label: status, cls: 'bg-slate-50 text-slate-600' };
  }
}

/**
 * Afhandelaar-cel. De voice-agent krijgt bewust de enige gekleurde pill: dat
 * is de kolom waar je op scant zolang de agent in proef draait. Een mens toont
 * zijn naam, de rest blijft grijs omdat "niemand nam op" al uit de status blijkt.
 */
function afhandelingLabel(a: Afhandeling | null): { text: string; cls: string } {
  switch (a?.soort) {
    case 'agent': return { text: 'Voice-agent', cls: 'bg-violet-50 text-violet-700 font-medium' };
    case 'medewerker': return { text: a.naam ?? 'Medewerker', cls: 'text-slate-700' };
    case 'voicemail': return { text: 'Voicemail', cls: 'text-slate-500' };
    case 'doorgeschakeld': return { text: 'Doorgeschakeld', cls: 'text-slate-500' };
    case 'gemist': return { text: 'Niemand', cls: 'text-slate-400' };
    default: return { text: '-', cls: 'text-slate-300' };
  }
}

/** Klein pill-label dat account (bestaande klant) van lead (offerte) onderscheidt. */
function customerTypeBadge(type: 'account' | 'lead' | null): { label: string; cls: string } | null {
  if (type === 'account') return { label: 'Klant', cls: 'bg-slate-100 text-slate-500' };
  if (type === 'lead') return { label: 'Lead', cls: 'bg-amber-50 text-amber-700' };
  return null;
}

interface CallHistoryListProps {
  onSelect: (callId: string) => void;
  selectedCallId: string | null;
}

export function CallHistoryList({ onSelect, selectedCallId }: CallHistoryListProps) {
  const [contextRow, setContextRow] = useState<HistoryRow | null>(null);
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (filters.q) params.set('q', filters.q);
      if (filters.brand) params.set('brand', filters.brand);
      if (filters.status) params.set('status', filters.status);
      if (filters.direction) params.set('direction', filters.direction);
      if (filters.afhandeling) params.set('afhandeling', filters.afhandeling);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);

      const res = await fetch(`/api/telephony/history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: HistoryResponse = await res.json();
      setRows(data.calls);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { void fetchHistory(); }, [fetchHistory]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const headerColumns = useMemo(
    () => [
      { key: 'when', label: 'Datum/tijd', align: 'left' as const },
      { key: 'customer', label: 'Klant', align: 'left' as const },
      { key: 'number', label: 'Nummer', align: 'left' as const },
      { key: 'dir', label: 'Richting', align: 'center' as const },
      { key: 'brand', label: 'Merk', align: 'center' as const },
      { key: 'duration', label: 'Duur', align: 'right' as const },
      { key: 'afhandeling', label: 'Afgehandeld door', align: 'left' as const },
      { key: 'status', label: 'Status', align: 'center' as const },
      { key: 'quality', label: 'Kwaliteit', align: 'right' as const },
    ],
    [],
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Filters */}
      <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-2 bg-slate-50">
        <input
          type="search"
          value={filters.q}
          onChange={(e) => updateFilter('q', e.target.value)}
          placeholder="Zoek op nummer..."
          aria-label="Zoek in belhistorie"
          className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200"
        />
        <select
          value={filters.brand}
          onChange={(e) => updateFilter('brand', e.target.value)}
          aria-label="Filter op merk"
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-md"
        >
          {BRAND_OPTIONS.map((b) => (
            <option key={b} value={b}>{b || 'Alle merken'}</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => updateFilter('status', e.target.value)}
          aria-label="Filter op status"
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-md"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s ? statusBadge(s).label : 'Alle statussen'}</option>
          ))}
        </select>
        <select
          value={filters.direction}
          onChange={(e) => updateFilter('direction', e.target.value)}
          aria-label="Filter op richting"
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-md"
        >
          <option value="">Beide richtingen</option>
          <option value="inbound">Inkomend</option>
          <option value="outbound">Uitgaand</option>
        </select>
        <select
          value={filters.afhandeling}
          onChange={(e) => updateFilter('afhandeling', e.target.value)}
          aria-label="Filter op afhandelaar"
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-md"
        >
          {AFHANDELING_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => updateFilter('from', e.target.value)}
          aria-label="Vanaf datum"
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-md"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => updateFilter('to', e.target.value)}
          aria-label="Tot datum"
          className="px-2 py-1.5 text-sm border border-slate-200 rounded-md"
        />
        <button
          type="button"
          onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}
          className="text-xs text-slate-500 hover:text-slate-800 underline"
        >
          Reset
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {headerColumns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-3 py-2 font-medium text-slate-600 text-${col.align}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={headerColumns.length} className="px-3 py-8 text-center text-slate-400">
                  Laden...
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={headerColumns.length} className="px-3 py-8 text-center text-red-500">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={headerColumns.length} className="px-3 py-8 text-center text-slate-400">
                  Geen gesprekken gevonden
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const badge = statusBadge(row.status, row);
              const ql = qualityLabel(row.packetLossPct);
              const isSelected = row.id === selectedCallId;
              const number = row.direction === 'inbound' ? row.fromNumber : row.toNumber;
              return (
                <tr
                  key={row.id}
                  onClick={() => onSelect(row.id)}
                  className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${
                    isSelected ? 'bg-indigo-50' : ''
                  }`}
                >
                  <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                    {formatDateTime(row.startedAt)}
                  </td>
                  <td className="px-3 py-2 text-slate-800 max-w-[220px]">
                    {row.customerName ? (
                      <div className="flex items-center gap-1.5 min-w-0">
                        {row.customerId ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setContextRow(row); }}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline truncate text-left"
                            title={row.customerType === 'lead' ? 'Open offerte' : 'Open klant'}
                          >
                            {row.customerName}
                          </button>
                        ) : (
                          <span className="truncate">{row.customerName}</span>
                        )}
                        {(() => {
                          const tb = customerTypeBadge(row.customerType);
                          return tb ? (
                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${tb.cls}`}>
                              {tb.label}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    ) : (
                      <span className="text-slate-400">Onbekend</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600 font-mono text-xs whitespace-nowrap">
                    {number || '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      aria-label={row.direction === 'inbound' ? 'Inkomend' : 'Uitgaand'}
                      title={row.direction === 'inbound' ? 'Inkomend' : 'Uitgaand'}
                    >
                      {row.direction === 'inbound' ? '↓' : '↑'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-slate-500">
                    {row.brand}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">
                    {formatDuration(row.durationSeconds)}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {(() => {
                      const al = afhandelingLabel(row.afhandeling);
                      return row.afhandeling?.soort === 'agent' ? (
                        <span className={`px-2 py-0.5 rounded ${al.cls}`}>{al.text}</span>
                      ) : (
                        <span className={al.cls}>{al.text}</span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono text-xs ${ql.color}`}>
                    {ql.text}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
        <span>
          {total > 0
            ? `${(page - 1) * LIMIT + 1}-${Math.min(page * LIMIT, total)} van ${total}`
            : 'Geen resultaten'}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40"
          >
            Vorige
          </button>
          <span>
            Pagina {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40"
          >
            Volgende
          </button>
        </div>
      </div>

      {contextRow && (
        <TakenKlantDrawer
          customerId={contextRow.customerId}
          customerType={contextRow.customerType}
          phone={contextRow.customerPhone
            ?? (contextRow.direction === 'inbound' ? contextRow.fromNumber : contextRow.toNumber)}
          fallbackName={contextRow.customerName}
          onClose={() => setContextRow(null)}
        />
      )}
    </div>
  );
}
